import { useCallback, useEffect, useState } from "react"
import { makeStyles } from "@material-ui/core"
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers"
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date"
import DayjsUtils from "@date-io/dayjs"
import dayjs, { Dayjs } from "dayjs"

import {
  GetQuestionnaireSchedulesExpirationDocument,
  GetQuestionnaireSchedulesExpirationQuery,
  GetQuestionnaireSchedulesExpirationQueryVariables,
  useGetQuestionnaireExpirationQuery,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import OutlineContainer from "../molecules/OutlineContainer"
import TimePicker from "../molecules/TimePicker"
import DaysHoursMinutesPicker from "../molecules/DaysHoursMinutesPicker"

import {
  combineDateTime,
  applyMinutesToDateTime,
  splitDateAndTime,
  convertMinutesToDaysHoursMinutes,
} from "../../utils/dateTimeUtils"
import { colors } from "../../utils/theme"
import { PickerTab } from "../../utils/types"

const useStyles = makeStyles((theme) => ({
  inputContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "center",
    padding: theme.spacing(2),
  },
  focusVisible: {
    "&.Mui-focusVisible": {
      boxShadow: `0 0 0 2px ${colors.primary.periwinkle}`,
    },
  },
}))

interface ScheduleExpirationTimeInputsProps {
  pickerTab: PickerTab
  questionnaireId: number
  questionnaireScheduleId: number | undefined
  scheduledDate: dayjs.Dayjs | MaterialUiPickersDate
  scheduledTime: dayjs.Dayjs | MaterialUiPickersDate
  updateExpiration: (minutes: number) => void
}

export default function ScheduleExpirationTimeInputs({
  pickerTab,
  questionnaireId,
  questionnaireScheduleId,
  scheduledDate,
  scheduledTime,
  updateExpiration,
}: ScheduleExpirationTimeInputsProps) {
  const classes = useStyles()
  const [expirationDate, setExpirationDate] = useState<
    Dayjs | MaterialUiPickersDate
  >(scheduledDate)
  const [expirationTime, setExpirationTime] = useState<
    Dayjs | MaterialUiPickersDate
  >(dayjs())
  const [daysHoursMinutes, setDaysHoursMinutes] = useState<{
    days: number
    hours: number
    minutes: number
  }>({ days: 0, hours: 0, minutes: 0 })

  const graphqlClient = useGraphQLClient()
  const { data: expiration } = useGetQuestionnaireExpirationQuery(
    graphqlClient,
    { id: questionnaireId }
  )

  // Helper to apply minutes to the scheduled date and set corresponding state
  const applyMinutesToDate = useCallback(
    (minutes: number) => {
      const adjusted = applyMinutesToDateTime(dayjs(scheduledDate), minutes)
      const { date, time } = splitDateAndTime(adjusted)
      setExpirationDate(date)
      setExpirationTime(time)
    },
    [scheduledDate]
  )

  /**
   * Validates a numeric time-unit value for the expiration offset.
   *
   * @param value - String representation of the user-entered value.
   * @param type - The time unit being validated: "days", "hours", or "minutes".
   * @returns True if the parsed value is a number within the allowed range
   *          (0–28 days, 0–23 hours, 0–59 minutes); otherwise false.
   */
  function confirmValueIsValid(
    value: string,
    type: "days" | "hours" | "minutes"
  ): boolean {
    let valueAsNumber = parseInt(value, 10)
    if (isNaN(valueAsNumber)) {
      return false
    }
    if (type === "days") {
      return valueAsNumber >= 0 && valueAsNumber <= 28
    } else if (type === "hours") {
      return valueAsNumber >= 0 && valueAsNumber <= 23
    } else if (type === "minutes") {
      return valueAsNumber >= 0 && valueAsNumber <= 59
    } else {
      return false
    }
  }

  // Expiration date must be at least scheduled date
  useEffect(() => {
    if (pickerTab === PickerTab.ONE_TIME) {
      setExpirationDate(scheduledDate)
    }
  }, [scheduledDate, pickerTab])

  // Recalculate expiration for one time schedules based on picked start day + end day and times
  useEffect(() => {
    if (pickerTab === PickerTab.RECURRING) return

    const combinedExpiration = combineDateTime(expirationDate, expirationTime)
    if (!combinedExpiration) return
    const combinedScheduled = combineDateTime(scheduledDate, scheduledTime)
    if (!combinedScheduled) return

    const minutesDiff = combinedExpiration.diff(combinedScheduled, "minute")
    updateExpiration(minutesDiff)
  }, [
    updateExpiration,
    expirationTime,
    expirationDate,
    scheduledDate,
    scheduledTime,
    pickerTab,
  ])

  // Recalculate expiration for recurring schedules based on days/hours/minute
  useEffect(() => {
    if (pickerTab !== PickerTab.RECURRING) return

    const minutesDiff =
      daysHoursMinutes.days * 24 * 60 +
      daysHoursMinutes.hours * 60 +
      daysHoursMinutes.minutes

    updateExpiration(minutesDiff)
  }, [pickerTab, daysHoursMinutes, updateExpiration])

  // Retrieve expiration times for when users edit schedules
  useEffect(() => {
    if (scheduledDate == null) return

    // Scheduled override
    if (questionnaireScheduleId) {
      graphqlClient
        .request<
          GetQuestionnaireSchedulesExpirationQuery,
          GetQuestionnaireSchedulesExpirationQueryVariables
        >(GetQuestionnaireSchedulesExpirationDocument, {
          id: questionnaireScheduleId,
        })
        .then((res) => {
          if (res.questionnaire_schedules_by_pk?.minutes_to_expiration) {
            const minutes =
              res.questionnaire_schedules_by_pk.minutes_to_expiration

            if (minutes != null) {
              if (pickerTab === PickerTab.ONE_TIME) {
                applyMinutesToDate(minutes)
              } else {
                setDaysHoursMinutes(convertMinutesToDaysHoursMinutes(minutes))
                updateExpiration(minutes)
              }
            }
          }
        })
      return
    }

    // Fallback to questionnaire default (if available)
    const minutes = expiration?.questionnaires_by_pk?.minutes_to_expiration
    if (minutes != null) {
      if (pickerTab === PickerTab.ONE_TIME) {
        applyMinutesToDate(minutes)
      } else {
        setDaysHoursMinutes(convertMinutesToDaysHoursMinutes(minutes))
        updateExpiration(minutes)
      }
    }
  }, [
    questionnaireScheduleId,
    graphqlClient,
    expiration?.questionnaires_by_pk,
    scheduledDate,
    applyMinutesToDate,
    pickerTab,
    updateExpiration,
  ])

  return (
    <OutlineContainer id="expiration" label="Expiration">
      <div className={classes.inputContainer}>
        {pickerTab === PickerTab.RECURRING ? (
          <DaysHoursMinutesPicker
            value={daysHoursMinutes}
            onTimeUnitChange={(unit, value) => {
              if (!confirmValueIsValid(value.toString(), unit)) {
                setDaysHoursMinutes((prev) => ({
                  ...prev,
                  [unit]: 0,
                }))
                return
              }

              setDaysHoursMinutes((prev) => ({
                ...prev,
                [unit]: value,
              }))
            }}
          />
        ) : (
          <>
            <MuiPickersUtilsProvider utils={DayjsUtils}>
              <KeyboardDatePicker
                autoOk
                variant="inline"
                format="MM/DD/YYYY"
                label="Enter Date"
                value={expirationDate}
                onChange={(date) => setExpirationDate(date)}
                minDate={scheduledDate ?? undefined}
                minDateMessage="Date should not be before the scheduled assessment date"
                fullWidth
                inputVariant="outlined"
                KeyboardButtonProps={{ className: classes.focusVisible }}
              />
            </MuiPickersUtilsProvider>
            <TimePicker
              label="Enter Time"
              defaultTime={dayjs(expirationTime)}
              value={expirationTime}
              onTimeChange={setExpirationTime}
            />
          </>
        )}
      </div>
    </OutlineContainer>
  )
}
